import type { TestRouting } from "@repo/common";
import { deleteTestRouting, updateTestRouting } from "@repo/ui";
import { getFirestore } from "firebase/firestore";
import {
	Loader2,
	Pencil,
	Plus,
	Search,
	Smartphone,
	Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "./ui/alert-dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "./ui/table";

// This file doesn't exist in admin yet, but we can use a local helper for now
// or import from app if we had shared utils.
// Given current repo state, I will implement a robust local helper to match backend.
function normalizePhoneNumber(value: string): string | null {
	const cleaned = value.trim().replace(/[^\d+]/g, "");
	if (!cleaned) return null;

	if (cleaned.startsWith("+")) return cleaned;
	if (cleaned.startsWith("0")) return `+972${cleaned.slice(1)}`;
	if (cleaned.startsWith("972")) return `+${cleaned}`;
	return `+${cleaned}`;
}

export function TestRoutingPage() {
	const { testRouting, agents, companies } = useData();
	const db = getFirestore();
	const [searchTerm, setSearchTerm] = useState("");
	const [isSaving, setIsSaving] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
	const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [selectedRouting, setSelectedRouting] = useState<TestRouting | null>(
		null,
	);

	const [formData, setFormData] = useState({
		phoneNumber: "",
		companyId: "",
		agentId: "",
	});

	const companiesById = useMemo(
		() => new Map(companies.map((c) => [c.id!, c])),
		[companies],
	);

	const agentsById = useMemo(
		() => new Map(agents.map((a) => [a.id, a])),
		[agents],
	);

	const filteredRoutings = useMemo(() => {
		const search = searchTerm.toLowerCase();
		return testRouting.filter((r) => {
			const agent = agentsById.get(r.agentId);
			const company = companiesById.get(r.companyId);
			const haystacks = [r.phoneNumber, agent?.name || "", company?.name || ""];
			return haystacks.some((v) => v.toLowerCase().includes(search));
		});
	}, [testRouting, searchTerm, agentsById, companiesById]);

	const availableAgents = useMemo(() => {
		if (!formData.companyId) return [];
		return agents.filter((a) => a.companyId === formData.companyId);
	}, [agents, formData.companyId]);

	const handleAddClick = () => {
		setFormData({ phoneNumber: "", companyId: "", agentId: "" });
		setIsAddDialogOpen(true);
	};

	const handleEditClick = (routing: TestRouting) => {
		setSelectedRouting(routing);
		setFormData({
			phoneNumber: routing.phoneNumber,
			companyId: routing.companyId,
			agentId: routing.agentId,
		});
		setIsEditDialogOpen(true);
	};

	const handleSaveSubmit = async () => {
		if (!formData.phoneNumber || !formData.companyId || !formData.agentId) {
			toast.error("Please fill in all fields");
			return;
		}

		setIsSaving(true);
		try {
			// Robust normalization to match backend
			const normalized = normalizePhoneNumber(formData.phoneNumber);
			if (!normalized) {
				toast.error("Please enter a valid phone number");
				setIsSaving(false);
				return;
			}

			await updateTestRouting(db, normalized, {
				agentId: formData.agentId,
				companyId: formData.companyId,
			});

			toast.success(`Routing for ${normalized} updated`);
			setIsAddDialogOpen(false);
			setIsEditDialogOpen(false);
			setFormData({ phoneNumber: "", companyId: "", agentId: "" });
		} catch (error) {
			console.error("Failed to save test routing", error);
			toast.error("Failed to save routing");
		} finally {
			setIsSaving(false);
		}
	};

	const handleDeleteClick = (routing: TestRouting) => {
		setSelectedRouting(routing);
		setIsDeleteDialogOpen(true);
	};

	const handleDeleteConfirm = async () => {
		if (!selectedRouting) return;
		setIsDeleting(true);
		try {
			await deleteTestRouting(db, selectedRouting.phoneNumber);
			toast.success("Routing deleted");
			setIsDeleteDialogOpen(false);
		} catch (error) {
			console.error("Failed to delete routing", error);
			toast.error("Failed to delete routing");
		} finally {
			setIsDeleting(false);
		}
	};

	return (
		<div className="space-y-6">
			<div className="flex justify-between items-start">
				<div>
					<h1>Test Routing Registry</h1>
					<p className="text-slate-600">
						Manage deterministic routing for test calls by phone number
					</p>
				</div>
				<Button onClick={handleAddClick}>
					<Plus className="mr-2 h-4 w-4" />
					Add Override
				</Button>

				{/* Add Dialog */}
				<Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
					<DialogContent className="admin-editor-dialog admin-editor-dialog--compact">
						<DialogHeader>
							<DialogTitle>Add Test Routing Override</DialogTitle>
							<DialogDescription>
								Direct calls from a specific number to a chosen agent.
							</DialogDescription>
						</DialogHeader>
						<div className="admin-editor-body space-y-4">
							<div className="space-y-2">
								<Label htmlFor="phone">Caller Phone Number (E.164)</Label>
								<Input
									id="phone"
									placeholder="+972541234567"
									value={formData.phoneNumber}
									onChange={(e) =>
										setFormData({ ...formData, phoneNumber: e.target.value })
									}
								/>
							</div>
							<div className="space-y-2">
								<Label>Company</Label>
								<Select
									value={formData.companyId}
									onValueChange={(val) =>
										setFormData({ ...formData, companyId: val, agentId: "" })
									}
								>
									<SelectTrigger>
										<SelectValue placeholder="Select company" />
									</SelectTrigger>
									<SelectContent>
										{companies.map((c) => (
											<SelectItem key={c.id} value={c.id!}>
												{c.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label>Target Agent</Label>
								<Select
									disabled={!formData.companyId}
									value={formData.agentId}
									onValueChange={(val) =>
										setFormData({ ...formData, agentId: val })
									}
								>
									<SelectTrigger>
										<SelectValue placeholder="Select agent" />
									</SelectTrigger>
									<SelectContent>
										{availableAgents.map((a) => (
											<SelectItem key={a.id} value={a.id}>
												{a.name || a.id}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setIsAddDialogOpen(false)}
							>
								Cancel
							</Button>
							<Button onClick={handleSaveSubmit} disabled={isSaving}>
								{isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
								Save Override
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Edit Dialog */}
				<Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
					<DialogContent className="admin-editor-dialog admin-editor-dialog--compact">
						<DialogHeader>
							<DialogTitle>Edit Test Routing Override</DialogTitle>
							<DialogDescription>
								Update routing target for {formData.phoneNumber}.
							</DialogDescription>
						</DialogHeader>
						<div className="admin-editor-body space-y-4">
							<div className="space-y-2">
								<Label htmlFor="edit-phone">Caller Phone Number (E.164)</Label>
								<Input
									id="edit-phone"
									value={formData.phoneNumber}
									disabled
									className="bg-slate-100"
								/>
							</div>
							<div className="space-y-2">
								<Label>Company</Label>
								<Select
									value={formData.companyId}
									onValueChange={(val) =>
										setFormData({ ...formData, companyId: val, agentId: "" })
									}
								>
									<SelectTrigger>
										<SelectValue placeholder="Select company" />
									</SelectTrigger>
									<SelectContent>
										{companies.map((c) => (
											<SelectItem key={c.id} value={c.id!}>
												{c.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label>Target Agent</Label>
								<Select
									disabled={!formData.companyId}
									value={formData.agentId}
									onValueChange={(val) =>
										setFormData({ ...formData, agentId: val })
									}
								>
									<SelectTrigger>
										<SelectValue placeholder="Select agent" />
									</SelectTrigger>
									<SelectContent>
										{availableAgents.map((a) => (
											<SelectItem key={a.id} value={a.id}>
												{a.name || a.id}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setIsEditDialogOpen(false)}
							>
								Cancel
							</Button>
							<Button onClick={handleSaveSubmit} disabled={isSaving}>
								{isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
								Save Changes
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Active Overrides</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="mb-4">
						<div className="relative">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
							<Input
								placeholder="Search by phone, agent or company..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="pl-10"
							/>
						</div>
					</div>

					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Caller Number</TableHead>
								<TableHead>Target Agent</TableHead>
								<TableHead>Company</TableHead>
								<TableHead>Last Updated</TableHead>
								<TableHead className="w-12"></TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{filteredRoutings.map((routing) => {
								const agent = agentsById.get(routing.agentId);
								const company = companiesById.get(routing.companyId);
								return (
									<TableRow key={routing.phoneNumber}>
										<TableCell className="font-medium">
											<div className="flex items-center gap-2">
												<Smartphone className="h-4 w-4 text-slate-400" />
												{routing.phoneNumber}
											</div>
										</TableCell>
										<TableCell>
											{agent ? (
												<Badge variant="default">
													{agent.name || agent.id}
												</Badge>
											) : (
												<span className="text-red-500 text-sm italic">
													Unknown Agent ({routing.agentId})
												</span>
											)}
										</TableCell>
										<TableCell>{company?.name || routing.companyId}</TableCell>
										<TableCell className="text-slate-500 text-sm">
											{(() => {
												if (!routing.updatedAt) return "N/A";
												try {
													const date =
														routing.updatedAt instanceof Date
															? routing.updatedAt
															: new Date(routing.updatedAt);
													return isNaN(date.getTime())
														? "N/A"
														: date.toLocaleString();
												} catch {
													return "N/A";
												}
											})()}
										</TableCell>
										<TableCell>
											<div className="flex gap-1">
												<Button
													variant="ghost"
													size="sm"
													onClick={() => handleEditClick(routing)}
												>
													<Pencil className="h-4 w-4" />
												</Button>
												<Button
													variant="ghost"
													size="sm"
													onClick={() => handleDeleteClick(routing)}
												>
													<Trash2 className="h-4 w-4 text-red-600" />
												</Button>
											</div>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>

					{filteredRoutings.length === 0 && (
						<div className="text-center py-8 text-slate-500">
							No overrides found
						</div>
					)}
				</CardContent>
			</Card>

			<AlertDialog
				open={isDeleteDialogOpen}
				onOpenChange={setIsDeleteDialogOpen}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Remove Override?</AlertDialogTitle>
						<AlertDialogDescription>
							This will remove the deterministic routing for{" "}
							{selectedRouting?.phoneNumber}. Test calls from this number will
							revert to automatic resolution.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleDeleteConfirm}
							disabled={isDeleting}
							className="bg-red-600 hover:bg-red-700"
						>
							{isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
							Delete
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
